import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormControl } from '@angular/forms';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { FilterDisplaySettings } from '../brand-filter-container.service';
import { AccountGroupMetricService } from '../../../metrics/account-group.service';
import { TaxonomyOption } from '@vendasta/taxonomy';
import { TranslateService } from '@ngx-translate/core';

export interface TaxonomyData {
  selectedTaxonomies: TaxonomyOption[];
  filterSettings: FilterDisplaySettings;
}

@Component({
  selector: 'bc-brand-geography-dialog',
  templateUrl: './brand-taxonomy-dialog.component.html',
  styleUrls: ['./brand-taxonomy-dialog.component.scss'],
  standalone: false,
})
export class BrandTaxonomyDialogComponent implements OnInit {
  taxonomyFormControl = new UntypedFormControl();

  taxonomyOptions$: Observable<any[]>;

  constructor(
    private accountGroupService: AccountGroupMetricService,
    private translateService: TranslateService,
    public dialogRef: MatDialogRef<BrandTaxonomyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TaxonomyData,
  ) {}

  ngOnInit(): void {
    const selectedTaxonomyIds = this.data.selectedTaxonomies.map((taxOpts) => taxOpts.id);
    this.taxonomyFormControl.setValue(selectedTaxonomyIds);

    this.taxonomyOptions$ = combineLatest([
      this.accountGroupService.brandTaxonomyOptions$,
      this.translateService.stream('FRONTEND.TAXONOMY'),
    ]).pipe(
      map(([brandTaxMap, taxonomyTranslations]) => {
        if (!brandTaxMap) {
          return [];
        }
        const locationsTemplate = this.translateService.instant('PERFORMANCE.MULTI_LOCATION.FILTERS.LOCATION_COUNT', {
          count: 'NUM',
        });
        return Array.from(brandTaxMap)
          .map(([fullTaxId, taxWithCount]) => {
            const taxNames = fullTaxId.split(':').map((taxId) => {
              return Object.prototype.hasOwnProperty.call(taxonomyTranslations, taxId)
                ? taxonomyTranslations[taxId]
                : taxId;
            });
            const taxName = taxNames.join(' > ');
            const numLocations = locationsTemplate.replace('NUM', taxWithCount.numLocations);
            return {
              value: fullTaxId,
              name: `${taxName} ${numLocations}`,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));
      }),
    );
  }

  close(newData: boolean): void {
    if (!newData) {
      this.dialogRef.close();
      return;
    }
    // Get the actual tax options back from the ids
    this.accountGroupService.brandTaxonomyOptions$
      .pipe(
        take(1),
        map((taxOptions) => {
          return (this.taxonomyFormControl.value || []).map((taxId) => {
            return taxOptions.get(taxId).taxonomyOption;
          });
        }),
      )
      .subscribe((selectedTaxonomies) => {
        this.dialogRef.close({
          selectedTaxonomies: selectedTaxonomies,
        });
      });
  }
}
